<template>
    <div>
        <div style="margin-bottom:7px;">
            Bump Chart: Ranking over time
            <span v-for="series in legend_items" style="margin-right:8px;" :key="series.name">
                <span
                    v-bind:style="{ borderBottom: '3px solid ' + series.color, padding:'3px', fontSize:'11px'}"
                    @mouseover="hover_series=series.name"
                    @mouseleave="hover_series=null">
                    {{series.name}}
                </span>
            </span>
        </div>
        <div ref="d3_root"></div>
    </div>
</template>

<script>
    import * as d3 from "d3";
    export default {
        data() {
            return {
                legend_items: [],
                hover_series: null,
                margin: { top: 20, right: 60, bottom: 40, left: 60 },
                series_data: [],
                x_scale: null,
                y_scale: null,
                color_scale: null,
                line_generator: null
            }
        },
        mounted() {
            if (this.chartData && this.chartData.datasets) {
                this.draw()
            }
        },
        watch: {
            chartData: function (newValue) {
                if (newValue) this.draw()
            },
            height: function(newValue) {
                if (newValue) this.draw()
            },
            text_size: function(newValue) {
                if (newValue) this.draw()
            },
            hover_series: function(newValue) {
                this.updateHighlight()
            }
        },
        props: ['height','width', 'text_size', 'chartData'],
        methods: {
            draw() {
                // Clear existing content
                while (this.$refs.d3_root.firstChild) {
                    this.$refs.d3_root.removeChild(this.$refs.d3_root.firstChild);
                }

                if (!this.chartData || !this.chartData.datasets) {
                    return;
                }

                // Process data from time series format to bump chart format
                this.processData();

                // Set up dimensions
                const innerWidth = this.width - this.margin.left - this.margin.right;
                const innerHeight = this.height - this.margin.top - this.margin.bottom;

                // Create SVG
                const svg = d3.select(document.createElementNS("http://www.w3.org/2000/svg",'svg'))
                    .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                    .style("font", `${5 + (this.text_size / 100) * 10}px sans-serif`);

                const g = svg.append("g")
                    .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

                // Set up scales
                const time_points = this.chartData.labels;
                const max_rank = this.series_data.length;

                this.x_scale = d3.scalePoint()
                    .domain(time_points)
                    .range([0, innerWidth]);

                this.y_scale = d3.scaleLinear()
                    .domain([1, max_rank])
                    .range([0, innerHeight]);

                this.color_scale = d3.scaleOrdinal()
                    .domain(this.series_data.map(d => d.name))
                    .range(this.series_data.map(d => d.color));


                this.line_generator = d3.line()
                    .x(d => this.x_scale(d.time))
                    .y(d => this.y_scale(d.rank))
                    .curve(d3.curveMonotoneX);

                // Draw axes
                this.drawAxes(g, time_points, max_rank, innerWidth, innerHeight);

                // Draw bump chart lines and points
                this.drawBumpChart(g);

                // Append to DOM
                this.$refs.d3_root.appendChild(svg.node());
            },

            processData() {
                // Transform time series data to bump chart format
                const datasets = this.chartData.datasets;
                const labels = this.chartData.labels;
                
                // Create series data with rankings
                this.series_data = datasets.map((dataset, index) => {
                    // Extract color and ensure it's visible (not transparent)
                    let color = dataset.borderColor || dataset.backgroundColor || '#3498db';
                    
                    // If color is transparent (alpha = 0), generate a visible color
                    if (color && (color.includes('rgba') && color.includes(', 0)'))) {
                        // Generate a color from a palette
                        const colorPalette = [
                            '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
                            '#e67e22', '#1abc9c', '#34495e', '#e91e63', '#ff9800'
                        ];
                        color = colorPalette[index % colorPalette.length];
                    }
                    
                    return {
                        name: dataset.label,
                        color: color,
                        data: dataset.data
                    };
                });

                this.legend_items = this.series_data.map(d => ({
                    name: d.name,
                    color: d.color
                }));

                // Calculate rankings for each time point
                labels.forEach((label, timeIndex) => {
                    // Get values for this time point and sort to determine ranks
                    const timeValues = this.series_data.map((series, seriesIndex) => ({
                        seriesIndex,
                        value: series.data[timeIndex] || 0,
                        name: series.name
                    })).sort((a, b) => b.value - a.value); // Sort descending for rank 1 = highest value


                    // Assign ranks
                    timeValues.forEach((item, rank) => {
                        if (!this.series_data[item.seriesIndex].ranking_data) {
                            this.series_data[item.seriesIndex].ranking_data = [];
                        }
                        this.series_data[item.seriesIndex].ranking_data.push({
                            time: label,
                            rank: rank + 1,
                            value: item.value
                        });
                    });
                });
            },

            drawAxes(g, time_points, max_rank, innerWidth, innerHeight) {
                // X-axis (time)
                g.append("g")
                    .attr("class", "x-axis")
                    .attr("transform", `translate(0,${innerHeight})`)
                    .call(d3.axisBottom(this.x_scale))
                    .selectAll("text")
                    .style("text-anchor", "end")
                    .attr("dx", "-.8em")
                    .attr("dy", ".15em")
                    .attr("transform", "rotate(-45)");

                // Y-axis (rank)
                const y_axis = d3.axisLeft(this.y_scale)
                    .tickFormat(d => `#${d}`)
                    .ticks(Math.min(max_rank, 10));

                g.append("g")
                    .attr("class", "y-axis")
                    .call(y_axis);

                // Y-axis label
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", 0 - this.margin.left)
                    .attr("x", 0 - (innerHeight / 2))
                    .attr("dy", "1em")
                    .style("text-anchor", "middle")
                    .style("font-size", "12px")
                    .style("fill", "#666")
                    .text("Rank");
            },

            drawBumpChart(g) {
                const series_groups = g.selectAll(".series")
                    .data(this.series_data)
                    .enter()
                    .append("g")
                    .attr("class", "series")
                    .attr("data-series", d => d.name);

                // Draw lines
                series_groups.append("path")
                    .attr("class", "line")
                    .attr("d", d => this.line_generator(d.ranking_data))
                    .attr("fill", "none")
                    .attr("stroke", d => d.color || '#ff0000')
                    .attr("stroke-width", 2)
                    .attr("opacity", 0.8);

                // Draw points
                series_groups.selectAll(".point")
                    .data(d => d.ranking_data.map(point => ({...point, seriesName: d.name, color: d.color})))
                    .enter()
                    .append("circle")
                    .attr("class", "point")
                    .attr("cx", d => this.x_scale(d.time))
                    .attr("cy", d => this.y_scale(d.rank))
                    .attr("r", 8)
                    .attr("fill", d => d.color)
                    .attr("stroke", "white")
                    .attr("stroke-width", 2)
                    .on("mouseover", this.handleMouseOver)
                    .on("mouseout", this.handleMouseOut);

                // Add rank labels on points
                series_groups.selectAll(".rank-label")
                    .data(d => d.ranking_data.map(point => ({...point, seriesName: d.name, color: d.color})))
                    .enter()
                    .append("text")
                    .attr("class", "rank-label")
                    .attr("x", d => this.x_scale(d.time))
                    .attr("y", d => this.y_scale(d.rank))
                    .attr("dy", "0.35em")
                    .attr("text-anchor", "middle")
                    .style("font-size", "10px")
                    .style("font-weight", "bold")
                    .style("fill", "white")
                    .style("pointer-events", "none")
                    .text(d => d.rank);

                // Add series labels at the end
                series_groups.append("text")
                    .attr("class", "series-label")
                    .attr("x", this.x_scale(this.chartData.labels[this.chartData.labels.length - 1]) + 10)
                    .attr("y", d => this.y_scale(d.ranking_data[d.ranking_data.length - 1].rank))
                    .attr("dy", "0.35em")
                    .style("font-size", "11px")
                    .style("font-weight", "bold")
                    .style("fill", d => d.color)
                    .text(d => d.name);
            },

            updateHighlight() {
                const svg = d3.select(this.$refs.d3_root).select("svg");
                
                if (this.hover_series) {
                    // Highlight selected series
                    svg.selectAll(".series")
                        .style("opacity", d => d.name === this.hover_series ? 1 : 0.2);
                } else {
                    // Reset all series
                    svg.selectAll(".series")
                        .style("opacity", 1);
                }
            },

            handleMouseOver(event, d) {
                // Create tooltip
                const tooltip = d3.select("body").append("div")
                    .attr("class", "bump-chart-tooltip")
                    .style("position", "absolute")
                    .style("background", "rgba(0, 0, 0, 0.8)")
                    .style("color", "white")
                    .style("padding", "8px")
                    .style("border-radius", "4px")
                    .style("font-size", "12px")
                    .style("pointer-events", "none")
                    .style("z-index", 1000);

                tooltip.html(`
                    <strong>${d.seriesName}</strong><br/>
                    Time: ${d.time}<br/>
                    Rank: #${d.rank}<br/>
                    Value: ${d.value.toLocaleString()}
                `);

                // Position tooltip
                const [mouseX, mouseY] = d3.pointer(event, document.body);
                tooltip
                    .style("left", (mouseX + 10) + "px")
                    .style("top", (mouseY - 10) + "px");

                // Highlight this series
                this.hover_series = d.seriesName;
            },

            handleMouseOut(event, d) {
                // Remove tooltip
                d3.selectAll(".bump-chart-tooltip").remove();
                
                // Reset highlight
                this.hover_series = null;
            }
        }
    }
</script>

<style scoped>
.series .line {
    transition: opacity 0.2s ease;
}

.series .point {
    transition: opacity 0.2s ease;
    cursor: pointer;
}

.series .point:hover {
    r: 7;
}
</style>